import { format } from 'date-fns'
import { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@/components/ui/button'
import { SingleDatePicker } from '@/components/single-date-picker'
import { Popover, PopoverTrigger } from '@/components/ui/popover'
import { Icons } from '@/theme/Images'
import * as PopoverPrimitive from '@radix-ui/react-popover'

const DatePickerInput = ({
  label,
  value,
  onChange,
  placeholder,
  helpText,
  className,
  disableFuture = false,
  disabled = false,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef(null)

  const handleCancel = () => {
    setIsVisible(false)
  }

  const handleOpenChange = open => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // Calculate optimal position
      let top = rect.bottom + 8 // Default: below the input
      let left = rect.left

      // If not enough space below, position above
      if (top + 400 > viewportHeight) {
        top = rect.top - 400 - 8
      }

      // If not enough space on the right, adjust left
      if (left + 300 > viewportWidth) {
        left = viewportWidth - 300 - 40
      }

      // Ensure it doesn't go off the left edge
      if (left < 40) {
        left = 40
      }

      // Ensure it doesn't go off the top edge
      if (top < 40) {
        top = 40
      }

      setCalendarPosition({ top, left })
    }
    setIsVisible(open)
  }

  // Simple backdrop click handler
  const handleBackdropClick = () => {
    setIsVisible(false)
  }

  return (
    <Popover open={isVisible} onOpenChange={handleOpenChange}>
      <div className="flex flex-col items-start self-stretch gap-1.5">
        <p className="text-sm font-medium text-[--gray-700]">{label}</p>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            data-testid="date-selector"
            variant="outline"
            role="combobox"
            aria-expanded={isVisible}
            disabled={disabled}
            className={`justify-between w-full h-10 text-base font-light rounded-md border bg-white pl-3 hover:bg-gray-100 ${className || ''}`}
            style={{
              boxShadow: '0 1px 2px 0 rgba(16, 24, 40, 0.05)',
              border: className?.includes('border-red-500')
                ? '1px solid #ef4444'
                : '1px solid var(--Gray-300, #D0D5DD)',
            }}
          >
            <span className="flex justify-between items-center w-full">
              {value ? (
                <p className="text-[--Gray-900] font-light">
                  {typeof value === 'string'
                    ? format(new Date(value + 'T00:00:00'), 'MM/dd/yyyy')
                    : format(value, 'MM/dd/yyyy')}
                </p>
              ) : (
                <p className="text-[#667085] font-light">{placeholder}</p>
              )}
              <img src={Icons.calendarRulesFormIcon} alt="Calendar Icon" className="mr-[-4px] w-5 h-5" />
            </span>
          </Button>
        </PopoverTrigger>
        {helpText}
      </div>
      {isVisible && (
        <>
          {/* Transparent backdrop that covers the entire screen */}
          <div className="fixed inset-0 z-40 bg-transparent" onClick={handleBackdropClick} />
          <PopoverPrimitive.Portal>
            <div
              className="bg-white rounded-md border shadow-lg p-0 z-50"
              style={{
                position: 'fixed',
                top: `${calendarPosition.top}px`,
                left: `${calendarPosition.left}px`,
                maxHeight: 'calc(100vh - 80px)',
                maxWidth: 'calc(100vw - 80px)',
                overflow: 'auto',
              }}
            >
              <SingleDatePicker
                mode="single"
                selected={typeof value === 'string' ? new Date(value + 'T00:00:00') : value}
                onSelect={onChange}
                onClose={handleCancel}
                initialFocus
                disableFuture={disableFuture}
              />
            </div>
          </PopoverPrimitive.Portal>
        </>
      )}
    </Popover>
  )
}

export { DatePickerInput }

DatePickerInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  helpText: PropTypes.node,
  className: PropTypes.string,
  disableFuture: PropTypes.bool,
  disabled: PropTypes.bool,
}
